import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, img, list, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ProductCard } from "@/components/site/ProductCard";
import { Eyebrow, Reveal } from "@/components/site/Reveal";

const CLAIM_COLORS = {
  Pending: "text-[#d8b4fe] border-[#a855f7]/50",
  Contacted: "text-sky-300 border-sky-400/50",
  Reserved: "text-amber-300 border-amber-400/50",
  Completed: "text-emerald-300 border-emerald-400/50",
  Cancelled: "text-red-300 border-red-400/50",
};

export const ClaimStatus = ({ s }) => <span className={`font-mono-j rounded-full border px-3 py-1 text-[11px] uppercase tracking-widest ${CLAIM_COLORS[s] || CLAIM_COLORS.Pending}`}>{s}</span>;

const Claims = ({ claims }) => !claims.length ? (
  <div className="py-20 text-center" data-testid="claims-empty">
    <p className="font-display text-3xl">No claims yet.</p>
    <Link to="/shop" className="mt-4 inline-block text-[#d8b4fe] underline underline-offset-8">Browse the shop</Link>
  </div>
) : (
  <ul className="divide-y divide-[#281245] border-y border-[#281245]">
    {claims.map((c) => (
      <li key={c.id} className="flex items-center gap-5 py-5" data-testid={`my-claim-${c.id}`}>
        <img src={img(c.product_image)} alt="" className="h-20 w-16 shrink-0 object-cover" />
        <div className="min-w-0 flex-1">
          <Link to={`/item/${c.product_id}`} className="block truncate text-[#f3eefc] hover:text-[#d8b4fe]">{c.product_title}</Link>
          <p className="font-mono-j mt-1 text-xs text-[#a78bce]">{new Date(c.created_at).toLocaleDateString("en-CA")} · {money(c.product_price)}</p>
        </div>
        <ClaimStatus s={c.status} />
        {c.status === "Completed" && <Link to={`/item/${c.product_id}`} className="shrink-0 text-xs text-[#d8b4fe] underline underline-offset-4" data-testid={`write-review-link-${c.id}`}>Write a review</Link>}
      </li>
    ))}
  </ul>
);

export default function Account() {
  const { user } = useAuth();
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") === "saved" ? "saved" : "claims";
  const [claims, setClaims] = useState([]);
  const [saved, setSaved] = useState([]);

  useEffect(() => {
    api.get("/claims/me").then((r) => setClaims(list(r))).catch(() => {});
  }, []);
  useEffect(() => {
    api.get("/wishlist").then((r) => setSaved(list(r))).catch(() => {});
  }, [user?.saved?.length]);

  const Tab = ({ id, label, n }) => (
    <button onClick={() => setSp(id === "saved" ? { tab: "saved" } : {})} data-testid={`account-tab-${id}`} className={`border-b-2 pb-3 text-sm transition-colors ${tab === id ? "border-[#a855f7] text-white" : "border-transparent text-[#a78bce] hover:text-white"}`}>
      {label} <span className="font-mono-j ml-1">{n}</span>
    </button>
  );

  return (
    <div className="mx-auto max-w-[1100px] px-5 pb-32 pt-32 md:px-10 md:pt-40" data-testid="account-page">
      <Reveal>
        <Eyebrow>Your account</Eyebrow>
        <h1 className="font-display mt-5 text-5xl text-[#f3eefc] md:text-7xl">Hello, <span className="italic text-[#d8b4fe]">{user?.name?.split(" ")[0]}</span>.</h1>
      </Reveal>
      <div className="mt-14 flex gap-8 border-b border-[#281245]"><Tab id="claims" label="My claims" n={claims.length} /><Tab id="saved" label="Saved" n={saved.length} /></div>
      <div className="mt-8">
        {tab === "claims" ? <Claims claims={claims} /> : saved.length ? (
          <div className="grid grid-cols-2 gap-x-4 gap-y-12 md:grid-cols-3">{saved.map((p) => <ProductCard key={p.id} p={p} />)}</div>
        ) : <p className="py-20 text-center text-[#cbbbe6]" data-testid="saved-empty">Tap the heart on any piece to save it here.</p>}
      </div>
    </div>
  );
}
